import React, { useEffect, useMemo, useState } from 'react';
import CryptoJS from 'crypto-js';
import api from '../api';
import './estilodashuser.css';

const gerarChavePadrao = (conta) =>
  CryptoJS.MD5(String(Number(conta) * 11 - 181818)).toString(
    CryptoJS.enc.Hex
  );

const GERADORES = {
  ea: gerarChavePadrao,
  bias: gerarChavePadrao,
  ragnarok: gerarChavePadrao,
};

function getStatusValue(value) {
  return Number(value) === 1 ? 1 : 0;
}

function normalizeProdutoKey(value) {
  const s = String(value || '').trim().toLowerCase();

  if (
    s === 'ea' ||
    s.includes('quantum trades ea') ||
    s === 'quantum trades' ||
    s.includes('quantumtrades ea')
  ) {
    return 'ea';
  }

  if (s === 'bias' || s.includes('quantumtrades bias')) {
    return 'bias';
  }

  if (s === 'ragnarok' || s.includes('quantumtrades ragnarok')) {
    return 'ragnarok';
  }

  return s;
}

function getTipoAssinatura(assinatura) {
  const t = String(assinatura?.tipo || '').toLowerCase();
  return t === 'pacote' ? 'pacote' : 'individual';
}

export default function AdicionarLicenca({ userId }) {
  const [assinaturas, setAssinaturas] = useState([]);
  const [licencas, setLicencas] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [pacotes, setPacotes] = useState([]);

  const [assinaturaId, setAssinaturaId] = useState('');
  const [produtoId, setProdutoId] = useState('');
  const [produtoCodigo, setProdutoCodigo] = useState('');

  const [conta, setConta] = useState('');
  const [chave, setChave] = useState('');
  const [tipo, setTipo] = useState('Demo');

  const [mensagem, setMensagem] = useState('');
  const [tipoMensagem, setTipoMensagem] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (userId) {
      carregarDados();
    }
  }, [userId]);

  const carregarDados = async () => {
    try {
      setCarregando(true);
      setMensagem('');
      setTipoMensagem('');

      const [respAss, respLic, respProd, respPac] = await Promise.all([
        api.get('/api/minhas-assinaturas'),
        api.get('/api/minhas-licencas'),
        api.get('/api/produtos-ativos'),
        api.get('/api/pacotes-ativos'),
      ]);

      setAssinaturas(respAss.data || []);
      setLicencas(respLic.data || []);
      setProdutos(respProd.data || []);
      setPacotes(respPac.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados para adicionar licença:', error);
      setMensagem('Erro ao carregar suas assinaturas e licenças. Tente novamente.');
      setTipoMensagem('erro');
    } finally {
      setCarregando(false);
    }
  };

  const assinaturasAtivas = useMemo(() => {
    return assinaturas.filter((a) => getStatusValue(a.status) === 1);
  }, [assinaturas]);

  const assinaturaSelecionada = useMemo(() => {
    return (
      assinaturasAtivas.find((a) => String(a.id) === String(assinaturaId)) ||
      null
    );
  }, [assinaturasAtivas, assinaturaId]);

  const produtosPermitidos = useMemo(() => {
    if (!assinaturaSelecionada) return [];

    const tipoAss = getTipoAssinatura(assinaturaSelecionada);
    const codigoPlano = String(assinaturaSelecionada.codigo_plano || '').trim();

    if (tipoAss === 'individual') {
      const codigoNormalizado = normalizeProdutoKey(codigoPlano);

      const produto = produtos.find(
        (p) =>
          normalizeProdutoKey(p.codigo) === codigoNormalizado ||
          normalizeProdutoKey(p.nome) === codigoNormalizado
      );

      if (!produto) return [];

      return [
        {
          id: Number(produto.id),
          codigo: produto.codigo,
          nome: produto.nome,
        },
      ];
    }

    const pacote = pacotes.find(
      (p) =>
        String(p.codigo || '').trim().toLowerCase() ===
        codigoPlano.toLowerCase()
    );

    if (!pacote || !Array.isArray(pacote.produtos)) return [];

    return pacote.produtos.map((p) => ({
      id: Number(p.id),
      codigo: p.codigo,
      nome: p.nome,
    }));
  }, [assinaturaSelecionada, produtos, pacotes]);

  useEffect(() => {
    if (!assinaturaSelecionada) {
      setProdutoId('');
      setProdutoCodigo('');
      return;
    }

    if (produtosPermitidos.length === 1) {
      const unico = produtosPermitidos[0];
      setProdutoId(String(unico.id));
      setProdutoCodigo(unico.codigo || '');
      return;
    }

    if (
      produtoId &&
      !produtosPermitidos.some((p) => String(p.id) === String(produtoId))
    ) {
      setProdutoId('');
      setProdutoCodigo('');
    }
  }, [assinaturaSelecionada, produtosPermitidos, produtoId]);

  const resumoProduto = useMemo(() => {
    if (!assinaturaSelecionada || !produtoId) {
      return {
        slotsContratados: 0,
        licencasAtivasProduto: 0,
        slotsDisponiveis: 0,
      };
    }

    const slotsContratados = Number(assinaturaSelecionada.slots_contratados || 0);

    const licencasAtivasProduto = licencas.filter(
      (l) =>
        getStatusValue(l.status) === 1 &&
        normalizeProdutoKey(l.produto_codigo || l.produto_nome) ===
          normalizeProdutoKey(produtoCodigo || '')
    ).length;

    const slotsDisponiveis = Math.max(
      slotsContratados - licencasAtivasProduto,
      0
    );

    return {
      slotsContratados,
      licencasAtivasProduto,
      slotsDisponiveis,
    };
  }, [assinaturaSelecionada, licencas, produtoId, produtoCodigo]);

  const resumoGeral = useMemo(() => {
    const licencasAtivas = licencas.filter((l) => getStatusValue(l.status) === 1);

    return {
      totalAssinaturasAtivas: assinaturasAtivas.length,
      totalLicencas: licencas.length,
      totalLicencasAtivas: licencasAtivas.length,
    };
  }, [assinaturasAtivas, licencas]);

  const handleTrocarAssinatura = (value) => {
    setAssinaturaId(value);
    setProdutoId('');
    setProdutoCodigo('');
    setConta('');
    setChave('');
    setMensagem('');
    setTipoMensagem('');
  };

  const handleTrocarProduto = (value) => {
    const produto = produtosPermitidos.find(
      (p) => String(p.id) === String(value)
    );

    setProdutoId(value);
    setProdutoCodigo(produto?.codigo || '');
    setChave('');
    setMensagem('');
    setTipoMensagem('');
  };

  const handleGerarChave = () => {
    if (!produtoCodigo) {
      setMensagem('Selecione primeiro o produto/robô da licença.');
      setTipoMensagem('erro');
      return;
    }

    if (!conta) {
      setMensagem('Informe o número da conta MT5.');
      setTipoMensagem('erro');
      return;
    }

    const contaNum = Number(conta);
    if (Number.isNaN(contaNum)) {
      setMensagem('Conta inválida. Use apenas números.');
      setTipoMensagem('erro');
      return;
    }

    const produtoKey = normalizeProdutoKey(produtoCodigo);
    const fn = GERADORES[produtoKey] || GERADORES.ea;

    setChave(fn(conta));
    setMensagem('');
    setTipoMensagem('');
  };

  const handleSalvarLicenca = async () => {
    if (salvando) return;

    if (!assinaturaId) {
      setMensagem('Selecione a assinatura que será usada para esta licença.');
      setTipoMensagem('erro');
      return;
    }

    if (!produtoId) {
      setMensagem('Selecione o produto/robô da licença.');
      setTipoMensagem('erro');
      return;
    }

    if (!conta) {
      setMensagem('Informe o número da conta MT5.');
      setTipoMensagem('erro');
      return;
    }

    if (!chave) {
      setMensagem('Gere a chave antes de salvar.');
      setTipoMensagem('erro');
      return;
    }

    if (resumoProduto.slotsDisponiveis <= 0) {
      setMensagem(
        'Você já utilizou todos os slots disponíveis para este produto nesta assinatura. Entre em contato com o suporte se precisar ampliar seu plano.'
      );
      setTipoMensagem('erro');
      return;
    }

    const contaDuplicadaMesmoProduto = licencas.find(
      (l) =>
        String(l.conta || '') === String(conta) &&
        normalizeProdutoKey(l.produto_codigo || l.produto_nome) ===
          normalizeProdutoKey(produtoCodigo)
    );

    if (contaDuplicadaMesmoProduto) {
      setMensagem('Já existe uma licença para essa conta neste produto.');
      setTipoMensagem('erro');
      return;
    }

    try {
      setSalvando(true);

      await api.post('/api/minhas-licencas', {
        assinatura_id: Number(assinaturaId),
        produto_id: Number(produtoId),
        conta: String(conta).trim(),
        lkey: String(chave).trim(),
        tipo,
      });

      setMensagem(
        'Licença adicionada com sucesso. Sua conta MT5 já pode ser usada conforme a contratação ativa.'
      );
      setTipoMensagem('sucesso');

      setConta('');
      setChave('');
      setTipo('Demo');

      await carregarDados();
    } catch (error) {
      console.error('Erro ao salvar licença:', error);
      setMensagem(
        error.response?.data?.message || 'Erro ao salvar licença. Tente novamente.'
      );
      setTipoMensagem('erro');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="adicionar-licenca-container">
      <center>
        <h3>Adicionar Licença</h3>
        <p>
          Escolha a assinatura ativa, selecione o robô liberado nessa contratação,
          informe sua conta MT5, gere a chave e salve a licença.
        </p>
        <p>
          Cada assinatura libera um número de slots por produto. Em pacotes, cada
          robô possui seu próprio limite dentro do tier contratado.
        </p>
      </center>

      <div className="lic-kpis lic-kpis-compact">
        <div className="lic-kpi">
          <div className="lic-kpi-label">Assinaturas ativas</div>
          <div className="lic-kpi-value">{resumoGeral.totalAssinaturasAtivas}</div>
        </div>
        <div className="lic-kpi">
          <div className="lic-kpi-label">Licenças cadastradas</div>
          <div className="lic-kpi-value">{resumoGeral.totalLicencas}</div>
        </div>
        <div className="lic-kpi">
          <div className="lic-kpi-label">Licenças ativas</div>
          <div className="lic-kpi-value">{resumoGeral.totalLicencasAtivas}</div>
        </div>
        <div className="lic-kpi">
          <div className="lic-kpi-label">Disponíveis no produto</div>
          <div className="lic-kpi-value">
            {produtoId ? resumoProduto.slotsDisponiveis : '—'}
          </div>
        </div>
      </div>

      {mensagem && (
        <div
          className={
            tipoMensagem === 'erro'
              ? 'alert-box alert-error'
              : 'alert-box alert-success'
          }
        >
          {mensagem}
        </div>
      )}

      {carregando && <p className="lic-loading">Carregando dados...</p>}

      <form className="adicionar-licenca-form" onSubmit={(e) => e.preventDefault()}>
        <label htmlFor="assinatura">Assinatura</label>
        <select
          id="assinatura"
          value={assinaturaId}
          onChange={(e) => handleTrocarAssinatura(e.target.value)}
          disabled={salvando}
        >
          <option value="">Selecione a assinatura</option>
          {assinaturasAtivas.map((a) => (
            <option key={a.id} value={a.id}>
              {a.nome_plano || a.codigo_plano} ({a.tier}) -{' '}
              {getTipoAssinatura(a) === 'pacote' ? 'pacote' : 'individual'}
            </option>
          ))}
        </select>

        <label htmlFor="produto">Produto / Robô</label>
        <select
          id="produto"
          value={produtoId}
          onChange={(e) => handleTrocarProduto(e.target.value)}
          disabled={!assinaturaId || salvando}
        >
          <option value="">Selecione o produto</option>
          {produtosPermitidos.map((produto) => (
            <option key={produto.id} value={produto.id}>
              {produto.nome} ({produto.codigo})
            </option>
          ))}
        </select>

        {assinaturaSelecionada && produtoId && (
          <div className="subscription-mini-box">
            <div className="subscription-mini-title">Resumo da licença</div>
            <div>Assinatura: <strong>{assinaturaSelecionada.nome_plano || assinaturaSelecionada.codigo_plano}</strong></div>
            <div>Tier: <strong>{assinaturaSelecionada.tier}</strong></div>
            <div>Produto: <strong>{produtosPermitidos.find((p) => String(p.id) === String(produtoId))?.nome || '—'}</strong></div>
            <div>
              Slots deste produto: <strong>{resumoProduto.slotsContratados}</strong> •
              Em uso: <strong>{resumoProduto.licencasAtivasProduto}</strong> •
              Disponíveis: <strong>{resumoProduto.slotsDisponiveis}</strong>
            </div>
          </div>
        )}

        <label htmlFor="conta">Número da Conta MT5</label>
        <input
          type="text"
          id="conta"
          value={conta}
          onChange={(e) => setConta(e.target.value)}
          placeholder="Digite o número da conta"
          disabled={salvando}
        />

        <button
          type="button"
          onClick={handleGerarChave}
          className="botao-gerar-chave"
          disabled={!produtoId || salvando}
        >
          Gerar Chave
        </button>

        <label htmlFor="chave">Chave</label>
        <input
          type="text"
          id="chave"
          value={chave}
          readOnly
        />

        <label htmlFor="tipo">Tipo de Licença</label>
        <select
          id="tipo"
          value={tipo}
          onChange={(e) => setTipo(e.target.value)}
          className="select-tipo"
          disabled={salvando}
        >
          <option value="Real">Real</option>
          <option value="Demo">Demo</option>
        </select>

        <button
          type="button"
          onClick={handleSalvarLicenca}
          className="botao-salvar-licenca"
          disabled={!produtoId || resumoProduto.slotsDisponiveis <= 0 || salvando}
        >
          {salvando ? 'Salvando...' : 'Salvar Licença'}
        </button>
      </form>

      {licencas.length > 0 && (
        <div className="licencas-existentes">
          <h4>Licenças já cadastradas</h4>
          <table>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Conta</th>
                <th>Tipo</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {licencas.map((licenca) => (
                <tr key={licenca.id}>
                  <td>{licenca.produto_nome || licenca.produto_codigo || 'Produto'}</td>
                  <td>{licenca.conta}</td>
                  <td>{licenca.tipo || '—'}</td>
                  <td>
                    {getStatusValue(licenca.status) === 1 ? 'Ativa' : 'Inativa'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
